import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { Modal, type ConfirmSpec } from './ui';

/**
 * "Text people to cover…" (owner 10/21, prototype v187). In Mid-day change: a button on each left-early /
 * called-out line, a pop-up to pick who is texted, then who said "I can cover it" with "Give them the shift".
 * On the phone: the employee's "Open shift" card with one button, "I can cover it" (no "can't").
 */

export interface CoverDay {
  canGive: boolean;
  covers: { id: number; changeId: number; when: string; hours: number; asked: number; failed: number; sentAt: string; given: string | null; yes: { employeeId: number; name: string; at: string }[] }[];
}

export function useCoverDay(locationId: number, date: string) {
  const [d, setD] = useState<CoverDay | null>(null);
  const load = useCallback(async () => { try { setD(await get<CoverDay>(`/api/cover/day?location=${locationId}&date=${date}`)); } catch { setD(null); } }, [locationId, date]);
  useEffect(() => { void load(); const x = setInterval(() => void load(), 20_000); return () => clearInterval(x); }, [load]);
  return [d, load] as const;
}

/** Under one left-early line: the button, or what has happened since the texts went out. */
export function CoverLine({ open, cover, canGive, onPick, setConfirm, onGiven, showToast }: {
  open: { label: string } | null; cover: CoverDay['covers'][number] | undefined; canGive: boolean;
  onPick: () => void; setConfirm: (c: ConfirmSpec) => void; onGiven: () => Promise<void>; showToast: (m: string) => void;
}) {
  const [giving, setGiving] = useState<{ employeeId: number; name: string } | null>(null);
  if (!cover) return open && canGive ? <button className="tbtn" onClick={onPick} style={{ minHeight: 28, padding: '2px 10px', alignSelf: 'flex-start', marginLeft: 18 }}>Text people to cover {open.label}…</button> : null;
  void setConfirm;
  const give = (y: { employeeId: number; name: string }) => setGiving(y);
  return (
    <div className="coverbox">
      <span>{cover.given ? `✓ ${cover.given} is covering ${cover.when}. They were texted, and the others who were asked got a text saying it’s covered.`
        : `Texted ${cover.asked} ${cover.asked === 1 ? 'person' : 'people'} at ${cover.sentAt} to cover ${cover.when}.${cover.failed ? ` ${cover.failed} text${cover.failed === 1 ? '' : 's'} didn’t go through.` : ''}`}</span>
      {cover.yes.map((y) => (
        <div key={y.employeeId} className="coveryes"><b>{y.name}</b><span>can cover · said so at {y.at}</span><span style={{ flex: '1 1 auto' }} />
          {canGive && <button className="tbtn primary" onClick={() => give(y)} style={{ minHeight: 28, padding: '2px 10px' }}>Give them the shift</button>}</div>
      ))}
      {!cover.given && !cover.yes.length && <span className="stnone">Nobody has said they can cover it yet. Answers show here as they come in.</span>}
      {giving && <GiveModal cover={cover} who={giving} onClose={() => setGiving(null)} onDone={async (msg) => { setGiving(null); showToast(msg); await onGiven(); }} />}
    </div>
  );
}

function tlabel(b: number) {
  const m = 300 + 15 * b; const h = Math.floor(m / 60) % 24; const mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm}${h >= 12 ? 'p' : 'a'}`;
}

/** "Give them the shift": what happens, and an optional note that goes with their text. */
function GiveModal({ cover, who, onClose, onDone }: { cover: CoverDay['covers'][number]; who: { employeeId: number; name: string }; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const first = who.name.split(' ')[0];
  const go = async () => {
    setBusy(true);
    try { const r = await post<{ message: string }>(`/api/cover/${cover.id}/give`, { employeeId: who.employeeId, note: note.trim() || undefined }); await onDone(r.message); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); setBusy(false); }
  };
  return (
    <Modal title={`Give ${who.name} the shift?`} onClose={onClose}>
      <div style={{ fontSize: 14 }}>{who.name} will be added as called in, {cover.when} ({cover.hours} h), with breaks placed for them.</div>
      <div style={{ fontSize: 14 }}>{first} gets a text right away: “You’re covering {cover.when} today. Thanks!”{cover.asked > 1 ? ` The ${cover.asked - 1} other${cover.asked === 2 ? '' : 's'} who were asked get “The ${cover.when} shift has been covered. Thanks!”` : ''}</div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Add a message for {first} (optional, goes with the text)
        <textarea className="inp" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. Park in the back and come in through the side door." style={{ minHeight: 70 }} /></label>
      {err && <div className="errbar">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" disabled={busy} onClick={() => void go()}>Give them the shift</button>
      </div>
    </Modal>
  );
}

interface Candidates { original: { from: number; to: number; when: string }; from: number; to: number; when: string; dayWord: string; people: { id: number; name: string; level: number; weekHours: number }[]; text: string; configured: boolean }

/** The pop-up: who can be asked (fewest hours this week first), untick anyone, see the text, send. */
export function CoverPick({ changeId, onClose, onSent, showToast }: { changeId: number; onClose: () => void; onSent: () => Promise<void>; showToast: (m: string) => void }) {
  const [c, setC] = useState<Candidates | null>(null);
  const [err, setErr] = useState('');
  const [skip, setSkip] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  // "Adjust": a later start or earlier end than the rest of the shift (the list re-checks who's free then)
  const [hours, setHours] = useState<{ from: number; to: number } | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  useEffect(() => {
    get<Candidates>(`/api/cover/candidates?change=${changeId}${hours ? `&from=${hours.from}&to=${hours.to}` : ''}`).then((x) => { setC(x); setErr(''); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Can’t load the list.'));
  }, [changeId, hours]);
  const ids = (c?.people ?? []).filter((p) => !skip[p.id]).map((p) => p.id);
  const send = async () => {
    setBusy(true);
    try {
      const r = await post<{ sent: number; failed: number }>('/api/cover/send', { changeId, ids, ...(c ? { from: c.from, to: c.to } : {}) });
      showToast(`Texted ${r.sent} ${r.sent === 1 ? 'person' : 'people'}${r.failed ? ` · ${r.failed} didn’t go through` : ''}.`);
      await onSent(); onClose();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'The texts didn’t go out. Try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={c ? `Text people to cover ${c.when}` : 'Text people to cover'} onClose={onClose}>
      <div style={{ fontSize: 13, color: '#4A5561' }}>These people are off {c?.dayWord ?? 'today'}, aren’t on time off, haven’t marked those hours “Not available”, and get texts. Untick anyone you don’t want to ask. They answer in the app with “I can cover it”; you’ll see who can here, on the Mid-day change screen.</div>
      {c && (
        <div className="cvhours">
          <div><b>Shift to cover: {c.when}</b>{c.when !== c.original.when && <span className="stnone"> · the rest of the shift was {c.original.when}</span>}</div>
          {!adjusting ? <button className="btn2" onClick={() => setAdjusting(true)} style={{ minHeight: 30, padding: '2px 12px' }}>Adjust times</button> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Start
                <select className="sel-sm" value={c.from} aria-label="Start time for the open shift" onChange={(e) => setHours({ from: Number(e.target.value), to: Math.max(c.to, Number(e.target.value) + 4) })}>
                  {Array.from({ length: c.original.to - 4 - c.original.from + 1 }, (_, i) => c.original.from + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
                </select></label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>until
                <select className="sel-sm" value={c.to} aria-label="End time for the open shift" onChange={(e) => setHours({ from: c.from, to: Number(e.target.value) })}>
                  {Array.from({ length: c.original.to - (c.from + 4) + 1 }, (_, i) => c.from + 4 + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
                </select></label>
              {c.when !== c.original.when && <button className="elink" onClick={() => setHours(null)}>Back to {c.original.when}</button>}
            </div>
          )}
        </div>
      )}
      {err && <div className="errbar">{err}</div>}
      {c && !c.configured && <div className="sharebar" style={{ background: '#FCEFD9', borderColor: '#EBC98A', color: '#5C3A05' }}>Texting isn’t set up on this server yet (the Twilio settings), so nothing can be sent.</div>}
      {c && (
        <div className="cvlist">
          {c.people.map((p) => <label key={p.id} className="allday"><input type="checkbox" checked={!skip[p.id]} onChange={() => setSkip((s) => ({ ...s, [p.id]: !s[p.id] }))} />{p.name} · L{p.level} · {p.weekHours}h this week</label>)}
          {!c.people.length && <span className="stnone">Nobody fits: everyone is working, on time off, not available or can’t get texts.</span>}
        </div>
      )}
      {c && <><div style={{ fontSize: 12, color: '#4A5561' }}>The text they’ll get:</div><div className="bubble" style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div></>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" disabled={!ids.length || busy || !c?.configured} onClick={() => void send()}>{ids.length ? `Text ${ids.length} ${ids.length === 1 ? 'person' : 'people'}` : 'Text'}</button>
      </div>
    </Modal>
  );
}

/** Phone: open shifts this employee was asked to cover. */
export function CoverCards({ onChange }: { onChange?: () => void }) {
  const [cards, setCards] = useState<{ id: number; done: boolean; mine?: boolean; canSay: boolean; title: string; text: string }[]>([]);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setCards((await get<{ cards: typeof cards }>('/api/cover/mine')).cards); } catch { setCards([]); } }, []);
  useEffect(() => { void load(); const x = setInterval(() => void load(), 30_000); return () => clearInterval(x); }, [load]);
  const yes = async (id: number) => {
    try { await post(`/api/cover/${id}/yes`); setErr(''); await load(); onChange?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t go through. Try again.'); await load(); }
  };
  return (
    <>
      {cards.map((v) => (
        <div key={v.id} className={'covercard' + (v.done ? ' done' : v.mine ? ' mine' : '')} role="status">
          <b>{v.title}</b><span>{v.text}</span>
          {v.canSay && <button className="ebig" onClick={() => void yes(v.id)}>I can cover it</button>}
        </div>
      ))}
      {err && <div className="errbar">{err}</div>}
    </>
  );
}
